import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Tile, Loading } from "@/components/Bits";
import { Users, Plus, PencilSimple, Trash, FloppyDisk, X, Key, ShieldCheck } from "@phosphor-icons/react";

const ROLE_TONE = {
  admin:  { color: "var(--accent)",  bg: "rgba(237,32,39,0.10)" },
  sales:  { color: "#0D71DD",        bg: "rgba(13,113,221,0.10)" },
  viewer: { color: "var(--text-tertiary)", bg: "var(--bg-subtle)" },
};

const EMPTY_FORM = { email: "", name: "", role: "viewer", password: "" };

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const { lang } = useI18n();
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', user }

  const L = (fr, en) => (lang === "fr" ? fr : en);

  const load = () => api.get("/users").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);

  const onDelete = async (u) => {
    if (!window.confirm(L(`Supprimer l'utilisateur ${u.email} ?`, `Delete user ${u.email}?`))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(L("Utilisateur supprimé", "User deleted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec de la suppression", "Delete failed"));
    }
  };

  if (!rows) return <Tile testId="users-loading"><Loading /></Tile>;

  return (
    <>
      <Tile className="lg:col-span-2" testId="users-admin">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={16} weight="fill" style={{ color: "var(--accent)" }} />
            <h2 className="text-[15px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {L("Gestion des utilisateurs", "User management")}
            </h2>
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-full"
                  style={{ background: "var(--bg-subtle)", color: "var(--text-tertiary)" }}>
              {rows.length}
            </span>
          </div>
          <button
            onClick={() => setModal({ mode: "create", user: { ...EMPTY_FORM } })}
            data-testid="user-new-btn"
            className="btn-accent flex items-center gap-2"
          >
            <Plus size={13} weight="bold" />
            {L("Nouvel utilisateur", "New user")}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="tech w-full" data-testid="users-table">
            <thead>
              <tr>
                <th>{L("Email", "Email")}</th>
                <th>{L("Nom", "Name")}</th>
                <th>{L("Rôle", "Role")}</th>
                <th>{L("Créé", "Created")}</th>
                <th className="text-right">{L("Actions", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const tone = ROLE_TONE[u.role] || ROLE_TONE.viewer;
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="font-mono text-[12px]">{u.email}{isMe && <span className="ml-2 text-[10px] uppercase font-bold" style={{ color: "var(--accent)" }}>{L("vous", "you")}</span>}</td>
                    <td>{u.name}</td>
                    <td>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-wide"
                            style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.color}` }}>
                        {u.role === "admin" && <ShieldCheck size={10} weight="fill" />}
                        {u.role}
                      </span>
                    </td>
                    <td className="font-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setModal({ mode: "edit", user: { id: u.id, email: u.email, name: u.name, role: u.role, password: "" } })}
                          data-testid={`user-edit-${u.id}`}
                          className="p-1.5 rounded-[6px] hover:bg-[var(--bg-subtle)] transition-colors"
                          title={L("Modifier", "Edit")}
                        >
                          <PencilSimple size={13} />
                        </button>
                        <button
                          onClick={() => onDelete(u)}
                          disabled={isMe}
                          data-testid={`user-delete-${u.id}`}
                          className="p-1.5 rounded-[6px] hover:bg-[rgba(237,32,39,0.10)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={isMe ? L("Impossible de supprimer son propre compte", "Cannot delete yourself") : L("Supprimer", "Delete")}
                          style={{ color: isMe ? "var(--text-tertiary)" : "var(--accent)" }}
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Tile>

      {modal && <UserModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} L={L} />}
    </>
  );
}

function UserModal({ modal, onClose, onSaved, L }) {
  const isEdit = modal.mode === "edit";
  const [form, setForm] = useState(modal.user);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      if (isEdit) {
        const body = { name: form.name, role: form.role };
        if (form.password && form.password.trim()) body.password = form.password.trim();
        await api.put(`/users/${form.id}`, body);
        toast.success(L("Utilisateur mis à jour", "User updated"));
      } else {
        await api.post("/users", {
          email: form.email.trim().toLowerCase(),
          password: form.password,
          name: form.name?.trim() || undefined,
          role: form.role,
        });
        toast.success(L("Utilisateur créé", "User created"));
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec", "Failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="user-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-[16px] p-6"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", boxShadow: "var(--shadow-lg)" }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            {isEdit ? L("Modifier l'utilisateur", "Edit user") : L("Nouvel utilisateur", "New user")}
          </h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid="user-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="text-[11px] mb-1.5 font-mono uppercase tracking-wide" style={{ color: "var(--text-tertiary)" }}>
              Email
            </div>
            <input
              type="email"
              required={!isEdit}
              disabled={isEdit}
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              data-testid="user-input-email"
              className="w-full px-3 py-2 text-[13.5px] font-mono rounded-[10px]"
              style={{
                background: isEdit ? "var(--bg-subtle)" : "var(--bg-elevated)",
                border: "1px solid var(--border)",
                color: isEdit ? "var(--text-tertiary)" : "var(--text-primary)",
              }}
            />
          </label>

          <label className="block">
            <div className="text-[11px] mb-1.5 font-mono uppercase tracking-wide" style={{ color: "var(--text-tertiary)" }}>
              {L("Nom", "Name")}
            </div>
            <input
              type="text"
              value={form.name || ""}
              onChange={(e) => update("name", e.target.value)}
              placeholder={form.email?.split("@")[0]}
              data-testid="user-input-name"
              className="w-full px-3 py-2 text-[13.5px] rounded-[10px]"
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />
          </label>

          <label className="block">
            <div className="text-[11px] mb-1.5 font-mono uppercase tracking-wide" style={{ color: "var(--text-tertiary)" }}>
              {L("Rôle", "Role")}
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="user-role-picker">
              {["viewer", "sales", "admin"].map((r) => {
                const tone = ROLE_TONE[r];
                const active = form.role === r;
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={() => update("role", r)}
                    data-testid={`user-role-${r}`}
                    className="px-3 py-2 text-[12px] font-mono uppercase rounded-[10px] transition-all"
                    style={{
                      background: active ? tone.color : "var(--bg-elevated)",
                      color: active ? "white" : tone.color,
                      border: `1px solid ${tone.color}`,
                    }}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </label>

          <label className="block">
            <div className="text-[11px] mb-1.5 font-mono uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--text-tertiary)" }}>
              <Key size={11} />
              {isEdit
                ? L("Nouveau mot de passe (laisser vide pour ne pas changer)", "New password (leave empty to keep current)")
                : L("Mot de passe", "Password")}
            </div>
            <input
              type="password"
              required={!isEdit}
              minLength={isEdit ? 0 : 6}
              value={form.password || ""}
              onChange={(e) => update("password", e.target.value)}
              autoComplete="new-password"
              data-testid="user-input-password"
              placeholder={isEdit ? "••••••" : L("min. 6 caractères", "min 6 chars")}
              className="w-full px-3 py-2 text-[13.5px] font-mono rounded-[10px]"
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="user-modal-cancel">
            {L("Annuler", "Cancel")}
          </button>
          <button type="submit" disabled={saving} className="btn-accent flex items-center gap-2" data-testid="user-modal-save">
            <FloppyDisk size={13} weight="bold" />
            {saving ? L("Enregistrement…", "Saving…") : (isEdit ? L("Enregistrer", "Save") : L("Créer", "Create"))}
          </button>
        </div>
      </form>
    </div>
  );
}
